import { Box, Container, Stack, Typography } from '@mui/material';
import { Button } from '@classity-edtech/ui-base/dist/components/Inputs/Button/Button';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';

const INSTRUCTOR_ILLUSTRATION =
  'https://www.figma.com/api/mcp/asset/3d6455c0-19d6-4ca5-979b-b45102ebbdd0';
const LEARNER_ILLUSTRATION =
  'https://www.figma.com/api/mcp/asset/8f1cd53e-8ce8-497e-8274-c4d81d70dee2';

const RADIAL_BG_ORANGE_LARGE =
  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 240 240' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(0 -16.971 -16.971 0 120 120)'><stop stop-color='rgba(238,77,48,0.2)' offset='0'/><stop stop-color='rgba(119,39,24,0.1)' offset='0.35'/><stop stop-color='rgba(0,0,0,0)' offset='0.7'/></radialGradient></defs></svg>\")";
const RADIAL_BG_ORANGE_SMALL =
  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(0 -12.728 -12.728 0 90 90)'><stop stop-color='rgba(238,77,48,0.08)' offset='0'/><stop stop-color='rgba(0,0,0,0)' offset='0.7'/></radialGradient></defs></svg>\")";
const RADIAL_BG_LEARNER_MOBILE =
  "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 220 220' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(0 -15.556 -15.556 0 110 110)'><stop stop-color='rgba(238,77,48,0.08)' offset='0'/><stop stop-color='rgba(0,0,0,0)' offset='0.7'/></radialGradient></defs></svg>\")";

interface RoleBadgeProps {
  label: string;
  dark?: boolean;
}

const RoleBadge = ({ label, dark }: RoleBadgeProps) => (
  <Box
    sx={{
      transform: { xs: 'rotate(35deg)', md: 'rotate(-45deg)' },
      borderTop: `1px solid ${dark ? 'rgba(10,10,11,0.12)' : 'rgba(10,10,11,0.12)'}`,
      borderBottom: `1px solid ${dark ? 'rgba(10,10,11,0.12)' : 'rgba(10,10,11,0.12)'}`,
      backgroundColor: dark
        ? globalColours['$global-colour-secondary-brand-4']
        : 'rgba(255,255,255,0.7)',
      px: '39px',
      py: '6px',
      whiteSpace: 'nowrap',
    }}
  >
    <Typography
      variant={'body3SemiBold' as any}
      sx={{
        color: dark
          ? globalColours['$global-colour-base-contrast']
          : globalColours['$global-colour-content-secondary'],
        textAlign: 'center',
        display: 'block',
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface RoleCardProps {
  title: string;
  description: string;
  illustration?: string;
  badgeLabel: string;
  badgeDark?: boolean;
  dark?: boolean;
  onSignIn?: () => void;
  onCreateAccount?: () => void;
  /** Desktop only: show illustration at top */
  showIllustration?: boolean;
}

const RoleCard = ({
  title,
  description,
  illustration,
  badgeLabel,
  badgeDark,
  dark,
  onSignIn,
  onCreateAccount,
  showIllustration,
}: RoleCardProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;

  // Mobile badge positioning differs per card variant:
  // Educator (white/light): left 204px, top -24px, container 118.794px
  // Learner (dark): left 205.85px, top -28px, container 125.158px
  const mobileBadgeLeft = dark ? '205.85px' : '236px';
  const mobileBadgeTop = dark ? '-28px' : '-34px';
  const mobileBadgeSize = dark ? '125.158px' : '118.794px';

  return (
    <Box
      sx={{
        flex: { xs: 'none', md: '473.5 0 0' },
        width: { xs: '100%', md: 'auto' },
        minWidth: { md: 0 },
        borderRadius: '16px',
        border: dark ? 'none' : `1px solid rgba(10,10,11,0.12)`,
        backgroundColor: dark
          ? globalColours['$global-colour-secondary-brand-4']
          : globalColours['$global-colour-background-elevated'],
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Radial glow decorations */}
      {dark ? (
        // Learner (dark) mobile: single 220px glow at left:133.5px, top:-54px
        <Box
          sx={{
            position: 'absolute',
            top: isMobile ? '-54px' : '-60px',
            left: isMobile ? '133.5px' : '293.5px',
            width: '220px',
            height: '220px',
            borderRadius: '110px',
            backgroundImage: RADIAL_BG_LEARNER_MOBILE,
            pointerEvents: 'none',
          }}
        />
      ) : (
        // Educator (light) mobile: large 240px glow + small 180px glow
        <>
          <Box
            sx={{
              position: 'absolute',
              top: isMobile ? '-60px' : '-60px',
              left: isMobile ? '123.5px' : '293.5px',
              width: '240px',
              height: '240px',
              borderRadius: '120px',
              backgroundImage: RADIAL_BG_ORANGE_LARGE,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: isMobile ? '139px' : '333px',
              left: '-16px',
              width: '180px',
              height: '180px',
              borderRadius: '90px',
              backgroundImage: RADIAL_BG_ORANGE_SMALL,
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      {/* Badge corner decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: mobileBadgeTop, md: '-28px' },
          left: { xs: mobileBadgeLeft, md: '-28px' },
          width: { xs: mobileBadgeSize, md: '118.794px' },
          height: { xs: mobileBadgeSize, md: '118.794px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <RoleBadge label={badgeLabel} dark={badgeDark} />
      </Box>

      <Box sx={{ px: '28px', py: '32px', position: 'relative', zIndex: 1 }}>
        {/* Illustration — desktop only */}
        {showIllustration && illustration && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1,
              mb: '56px',
            }}
          >
            <Box
              component='img'
              src={illustration}
              alt=''
              sx={{ height: '220px', maxWidth: '260px', width: '260px', objectFit: 'contain' }}
              display='none'
            />
          </Box>
        )}

        {/* Text */}
        <Stack sx={{ gap: '6px', pb: '24px' }}>
          <Typography
            variant={'subtitle5' as any}
            sx={{
              color: dark
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-secondary'],
            }}
          >
            {title}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: dark
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-secondary'],
            }}
          >
            {description}
          </Typography>
        </Stack>

        {/* Actions */}
        <Stack sx={{ gap: '10px' }}>
          <Button
            variant='text'
            onClick={onSignIn}
            sx={{
              height: '50px',
              width: '100%',
              color: dark
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-secondary'],
              justifyContent: 'center',
            }}
          >
            <Typography variant='body2SemiBold'>Sign In</Typography>
          </Button>
          <Button
            variant='outlined'
            onClick={onCreateAccount}
            sx={{
              height: '50px',
              width: '100%',
              borderRadius: '100px',
              ':hover': {
                backgroundColor: dark
                  ? globalColours['$global-colour-background-screen-default']
                  : globalColours['$global-colour-secondary-brand-4'],
                borderColor: dark
                  ? 'rgba(205,205,209,0.28)'
                  : globalColours['$global-colour-interactive-secondary-default'],
                color: dark
                  ? globalColours['$global-colour-content-secondary']
                  : globalColours['$global-colour-base-contrast'],
              },
              backgroundColor: dark
                ? globalColours['$global-colour-secondary-brand-4']
                : globalColours['$global-colour-background-screen-default'],
              borderColor: dark
                ? 'rgba(205,205,209,0.28)'
                : globalColours['$global-colour-interactive-secondary-default'],
              color: dark
                ? globalColours['$global-colour-base-contrast']
                : globalColours['$global-colour-content-secondary'],
            }}
          >
            <Typography variant='body1'>Create an account</Typography>
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export interface AuthProps {
  onInstructorSignIn?: () => void;
  onInstructorCreateAccount?: () => void;
  onLearnerSignIn?: () => void;
  onLearnerCreateAccount?: () => void;
}

export const Auth = ({
  onInstructorSignIn,
  onInstructorCreateAccount,
  onLearnerSignIn,
  onLearnerCreateAccount,
}: AuthProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;

  return (
    <Box
      sx={{
        backgroundColor: globalColours['$global-colour-background-screen-default'],
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      <Container
        sx={{
          px: { xs: 2.5, md: 4 },
          py: { xs: 3, md: 2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Heading */}
        <Stack sx={{ alignItems: 'center', gap: '4px', mb: 4 }}>
          <Typography
            variant='body1'
            sx={{
              textAlign: 'center',
              color: globalColours['$global-colour-content-secondary'],
            }}
          >
            Welcome to Classity
          </Typography>
          <Typography
            variant={isMobile ? 'subtitle3' : 'subtitle2'}
            sx={{
              textAlign: 'center',
              color: globalColours['$global-colour-content-primary'],
            }}
          >
            How would you like to continue?
          </Typography>
        </Stack>

        {/* Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row-reverse' },
            gap: { xs: 3, md: 2 },
            pb: 3,
            px: 2,
            width: { xs: '100%', md: '75%' },
          }}
        >
          {/* Educator card */}
          <RoleCard
            title='Shape the next generation'
            description='Build courses, track learner progress, and grow your online community.'
            illustration={INSTRUCTOR_ILLUSTRATION}
            badgeLabel='Educator'
            badgeDark={true}
            dark={false}
            onSignIn={onInstructorSignIn}
            onCreateAccount={onInstructorCreateAccount}
            showIllustration={!isMobile}
          />

          {/* Learner card */}
          <RoleCard
            title='Keep learning, keep growing'
            description='Access your courses, track progress, and get reward points for your efforts.'
            illustration={LEARNER_ILLUSTRATION}
            badgeLabel='Learner'
            badgeDark={false}
            dark={!isMobile}
            onSignIn={onLearnerSignIn}
            onCreateAccount={onLearnerCreateAccount}
            showIllustration={!isMobile}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Auth;
